import { useParams } from 'react-router-dom';
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { RulesetConfig } from '@pandacalcio/validation';
import { useLeague } from '../../features/leagues/api';
import { useAnnouncer } from '../../components/LiveAnnouncer';
import { usePageTitle } from '../../lib/usePageTitle';
import { formatPoints } from '../../lib/dates';
import { AppLayout } from '../../components/AppLayout';
import { Button } from '../../components/Button';
import { Select } from '../../components/Select';
import { LeagueNav } from './LeagueNav';
import { api, ApiError } from '../../lib/api';

interface RulesetResponse {
  ruleset: {
    name: string;
    version: number;
    isSystem: boolean;
    config: RulesetConfig;
    canEdit: boolean;
  };
}

const TIER_LABELS: Record<string, string> = {
  '1-1': 'Prima contro Prima',
  '1-2': 'Prima contro Seconda',
  '1-3': 'Prima contro Terza',
  '2-1': 'Seconda contro Prima',
  '2-2': 'Seconda contro Seconda',
  '2-3': 'Seconda contro Terza',
  '3-1': 'Terza contro Prima',
  '3-2': 'Terza contro Seconda',
  '3-3': 'Terza contro Terza',
};

export function RulesetPage() {
  const { id = '' } = useParams();
  const league = useLeague(id);
  usePageTitle(league.data ? `Regolamento — ${league.data.name}` : 'Regolamento');
  const { announce } = useAnnouncer();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['leagues', id, 'ruleset'],
    queryFn: () => api<RulesetResponse>(`/leagues/${id}/ruleset`).then((r) => r.ruleset),
    enabled: !!id,
  });

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<RulesetConfig | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  if (query.isLoading) {
    return (
      <AppLayout title="Regolamento">
        <p role="status" className="mt-4">Caricamento regolamento…</p>
      </AppLayout>
    );
  }
  if (!query.data) {
    return (
      <AppLayout title="Regolamento">
        <p role="alert" className="mt-4 font-medium text-danger">Regolamento non disponibile.</p>
      </AppLayout>
    );
  }

  const { name, version, config, canEdit } = query.data;
  const c = editing && draft ? draft : config;

  const startEditing = () => {
    setDraft(structuredClone(config));
    setEditing(true);
    setError(null);
    announce('Modalità modifica regolamento attiva.');
  };

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    setError(null);
    try {
      const res = await api<{ message: string }>(`/leagues/${id}/ruleset`, {
        method: 'PUT',
        body: draft,
      });
      announce(res.message);
      setEditing(false);
      setDraft(null);
      queryClient.invalidateQueries({ queryKey: ['leagues', id, 'ruleset'] });
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Salvataggio non riuscito.';
      setError(msg);
      announce(`Errore: ${msg}`, 'assertive');
    } finally {
      setSaving(false);
    }
  };

  /** Aggiorna una sezione del draft. */
  const patch = <K extends keyof RulesetConfig>(key: K, value: Partial<RulesetConfig[K]>) => {
    if (!draft) return;
    setDraft({ ...draft, [key]: { ...draft[key], ...value } });
  };

  return (
    <AppLayout title={league.data ? `Regolamento — ${league.data.name}` : 'Regolamento'}>
      <LeagueNav leagueId={id} />

      <p className="mt-4 text-lg">
        <strong>{name}</strong> — versione {version}
      </p>
      {canEdit && !editing && (
        <div className="mt-3">
          <Button variant="secondary" onClick={startEditing}>
            Modifica il regolamento
          </Button>
          <p className="mt-2 max-w-prose text-sm text-ink-600 dark:text-ink-300">
            Le modifiche creano una nuova versione e valgono dalle prossime giornate: quelle già
            aperte o calcolate restano con le regole del momento dell’apertura.
          </p>
        </div>
      )}

      {error && (
        <p role="alert" className="mt-4 rounded-lg border-2 border-danger bg-red-50 p-3 font-medium text-danger dark:bg-ink-800">
          {error}
        </p>
      )}

      {/* ---------- SCHEDINA ---------- */}
      <section aria-labelledby="r-schedina" className="mt-8">
        <h2 id="r-schedina" className="text-2xl font-bold tracking-tight">La schedina</h2>
        {editing ? (
          <div className="mt-3 flex flex-wrap items-end gap-4">
            <Num
              label="Partite a pronostico fisso"
              value={c.picks.fixedCount}
              min={1}
              max={20}
              step={1}
              onChange={(v) => patch('picks', { fixedCount: v })}
            />
            <Check
              label="Doppie ammesse (1X, X2, 12)"
              checked={c.picks.allowDouble}
              onChange={(v) => patch('picks', { allowDouble: v })}
            />
            <Num
              label="La doppia divide i punti per"
              value={c.picks.doubleDivisor}
              min={1}
              max={10}
              step={0.5}
              onChange={(v) => patch('picks', { doubleDivisor: v })}
            />
          </div>
        ) : (
          <p className="mt-2 max-w-prose">
            Ogni concorrente pronostica <strong>{c.picks.fixedCount} partite</strong> (1, X o 2
            {c.picks.allowDouble && (
              <> — oppure doppia, che divide i punti per {formatPoints(c.picks.doubleDivisor)}</>
            )}
            )
            {c.exactResult.enabled && (
              <>
                , più il <strong>risultato esatto</strong> su una partita in più (+
                {formatPoints(c.exactResult.bonus)} se indovinato
                {c.exactResult.givesBasePoints && ', in aggiunta ai punti del segno'}; se il
                risultato non è esatto vale zero, anche col segno giusto)
              </>
            )}
            {c.over.enabled && (
              <>
                , e sceglie una partita per l’<strong>OVER</strong>
              </>
            )}
            .
          </p>
        )}
      </section>

      {/* ---------- MATRICE FASCE ---------- */}
      <section aria-labelledby="r-fasce" className="mt-8">
        <h2 id="r-fasce" className="text-2xl font-bold tracking-tight">Punti per fascia</h2>
        <p className="mt-1 max-w-prose text-ink-700 dark:text-ink-200">
          Dalla giornata {c.tiers.autoFromMatchday}: prime {c.tiers.topCount} squadre in prima
          fascia, ultime {c.tiers.bottomCount} in terza, le altre in seconda. Prima di allora tutte
          in seconda fascia.
        </p>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full max-w-xl border-collapse text-left">
            <caption className="visually-hidden">
              Punti assegnati per pronostico corretto secondo le fasce di casa e ospite
            </caption>
            <thead>
              <tr className="border-b-2 border-ink-300 dark:border-ink-600">
                <th scope="col" className="py-2 pr-3">Incontro (casa — ospite)</th>
                <th scope="col" className="py-2 pr-3 text-right">1</th>
                <th scope="col" className="py-2 pr-3 text-right">X</th>
                <th scope="col" className="py-2 text-right">2</th>
              </tr>
            </thead>
            <tbody>
              {(Object.keys(TIER_LABELS) as Array<keyof typeof TIER_LABELS>).map((key) => {
                const cell = c.tierMatrix[key as keyof RulesetConfig['tierMatrix']];
                return (
                  <tr key={key} className="border-b border-ink-200 dark:border-ink-800">
                    <th scope="row" className="py-2 pr-3 font-medium">{TIER_LABELS[key]}</th>
                    {(['home', 'draw', 'away'] as const).map((slot) => (
                      <td key={slot} className="py-2 pr-3 text-right">
                        {editing ? (
                          <input
                            type="number"
                            step={0.5}
                            min={-100}
                            max={100}
                            aria-label={`Punti per ${slot === 'home' ? '1' : slot === 'draw' ? 'X' : '2'} in ${TIER_LABELS[key]}`}
                            className="w-20 rounded-lg border border-ink-300 px-2 py-1.5 text-right dark:border-ink-600 dark:bg-ink-900"
                            value={cell[slot]}
                            onChange={(e) => {
                              if (!draft) return;
                              const matrix = { ...draft.tierMatrix };
                              matrix[key as keyof RulesetConfig['tierMatrix']] = {
                                ...cell,
                                [slot]: Number(e.target.value),
                              };
                              setDraft({ ...draft, tierMatrix: matrix });
                            }}
                          />
                        ) : (
                          <span className="font-semibold">{formatPoints(cell[slot])}</span>
                        )}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* ---------- BONUS / MALUS ---------- */}
      <section aria-labelledby="r-bonus" className="mt-8">
        <h2 id="r-bonus" className="text-2xl font-bold tracking-tight">Bonus e malus</h2>
        {editing ? (
          <div className="mt-3 flex flex-col gap-5">
            <fieldset className="flex flex-wrap items-end gap-4">
              <legend className="mb-2 font-semibold">Bonus tutte corrette</legend>
              <Check
                label="Attivo"
                checked={c.bonusAllCorrect.enabled}
                onChange={(v) => patch('bonusAllCorrect', { enabled: v })}
              />
              <Num label="Bonus" value={c.bonusAllCorrect.bonus} onChange={(v) => patch('bonusAllCorrect', { bonus: v })} />
              <Num label="Soglia punti dalle fisse" value={c.bonusAllCorrect.threshold} onChange={(v) => patch('bonusAllCorrect', { threshold: v })} />
              <Num label="Bonus sopra soglia (sostituisce)" value={c.bonusAllCorrect.bonusAboveThreshold} onChange={(v) => patch('bonusAllCorrect', { bonusAboveThreshold: v })} />
            </fieldset>
            <fieldset className="flex flex-wrap items-end gap-4">
              <legend className="mb-2 font-semibold">Malus nessuna corretta</legend>
              <Check label="Attivo" checked={c.malusNoneCorrect.enabled} onChange={(v) => patch('malusNoneCorrect', { enabled: v })} />
              <Num label="Penalità" value={c.malusNoneCorrect.penalty} min={-100} onChange={(v) => patch('malusNoneCorrect', { penalty: v })} />
            </fieldset>
            <fieldset className="flex flex-wrap items-end gap-4">
              <legend className="mb-2 font-semibold">Originalità</legend>
              <Check label="Attiva" checked={c.originality.enabled} onChange={(v) => patch('originality', { enabled: v })} />
              <Num label="Bonus pronostico unico corretto" value={c.originality.uniqueCorrectBonus} onChange={(v) => patch('originality', { uniqueCorrectBonus: v })} />
              <Num label="Da quanti uguali scatta la penalità" value={c.originality.sharedThreshold} min={2} step={1} onChange={(v) => patch('originality', { sharedThreshold: v })} />
              <Num label="Penalità" value={c.originality.sharedPenalty} min={-100} onChange={(v) => patch('originality', { sharedPenalty: v })} />
            </fieldset>
          </div>
        ) : (
          <ul className="mt-2 flex max-w-prose list-disc flex-col gap-1 pl-5">
            {c.bonusAllCorrect.enabled && (
              <li>
                {c.picks.fixedCount}/{c.picks.fixedCount} corrette:{' '}
                <strong>+{formatPoints(c.bonusAllCorrect.bonus)}</strong>; con almeno{' '}
                {formatPoints(c.bonusAllCorrect.threshold)} punti dalle fisse il bonus diventa{' '}
                <strong>+{formatPoints(c.bonusAllCorrect.bonusAboveThreshold)}</strong>
              </li>
            )}
            {c.malusNoneCorrect.enabled && (
              <li>
                0/{c.picks.fixedCount}: <strong>{formatPoints(c.malusNoneCorrect.penalty)}</strong>
              </li>
            )}
            {c.originality.enabled && (
              <li>
                Unico a indovinare un segno: <strong>+{formatPoints(c.originality.uniqueCorrectBonus)}</strong>;
                in {c.originality.sharedThreshold} o più con lo stesso segno corretto:{' '}
                <strong>{formatPoints(c.originality.sharedPenalty)}</strong> (doppie
                {c.originality.excludeBots ? ', bot' : ''} e colonne rosse escluse dal conteggio)
              </li>
            )}
          </ul>
        )}
      </section>

      {/* ---------- OVER ---------- */}
      <section aria-labelledby="r-over" className="mt-8">
        <h2 id="r-over" className="text-2xl font-bold tracking-tight">OVER</h2>
        {editing ? (
          <div className="mt-3 flex flex-col gap-4">
            <div className="flex flex-wrap items-end gap-4">
              <Check label="Attivo" checked={c.over.enabled} onChange={(v) => patch('over', { enabled: v })} />
              <Check label="Obbligatorio" checked={c.over.required} onChange={(v) => patch('over', { required: v })} />
              <Num label="Penalità sotto la prima soglia" value={c.over.penalty} min={-100} onChange={(v) => patch('over', { penalty: v })} />
            </div>
            <fieldset>
              <legend className="font-semibold">Livelli (gol totali → punti)</legend>
              <div className="mt-2 flex flex-col gap-2">
                {c.over.levels.map((level, i) => (
                  <div key={i} className="flex items-end gap-3">
                    <Num
                      label={`Livello ${i + 1}: gol`}
                      value={level.goals}
                      min={0}
                      step={1}
                      onChange={(v) => {
                        if (!draft) return;
                        const levels = draft.over.levels.map((l, j) => (j === i ? { ...l, goals: v } : l));
                        patch('over', { levels });
                      }}
                    />
                    <Num
                      label={`Livello ${i + 1}: punti`}
                      value={level.points}
                      onChange={(v) => {
                        if (!draft) return;
                        const levels = draft.over.levels.map((l, j) => (j === i ? { ...l, points: v } : l));
                        patch('over', { levels });
                      }}
                    />
                    {c.over.levels.length > 1 && (
                      <Button
                        variant="secondary"
                        onClick={() => {
                          if (!draft) return;
                          patch('over', { levels: draft.over.levels.filter((_, j) => j !== i) });
                        }}
                      >
                        Rimuovi
                        <span className="visually-hidden"> il livello {i + 1}</span>
                      </Button>
                    )}
                  </div>
                ))}
                <div>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      if (!draft) return;
                      const last = draft.over.levels.at(-1);
                      patch('over', {
                        levels: [
                          ...draft.over.levels,
                          { goals: (last?.goals ?? 2) + 2, points: (last?.points ?? 2) + 1 },
                        ],
                      });
                    }}
                  >
                    Aggiungi livello
                  </Button>
                </div>
              </div>
            </fieldset>
          </div>
        ) : c.over.enabled ? (
          <p className="mt-2 max-w-prose">
            {[...c.over.levels]
              .sort((a, b) => a.goals - b.goals)
              .map((l) => `da ${l.goals} gol +${formatPoints(l.points)}`)
              .join(', ')}
            ; sotto {Math.min(...c.over.levels.map((l) => l.goals))} gol{' '}
            <strong>{formatPoints(c.over.penalty)}</strong>.
            {c.over.required ? ' La scelta è obbligatoria.' : ''}
          </p>
        ) : (
          <p className="mt-2">Non previsto.</p>
        )}
      </section>

      {/* ---------- COLONNA ROSSA E CAPOCANNONIERE ---------- */}
      <section aria-labelledby="r-varie" className="mt-8">
        <h2 id="r-varie" className="text-2xl font-bold tracking-tight">Colonna rossa e capocannoniere</h2>
        {editing ? (
          <div className="mt-3 flex flex-col gap-5">
            <fieldset className="flex flex-wrap items-end gap-4">
              <legend className="mb-2 font-semibold">Colonna rossa (schedina non consegnata)</legend>
              <Check label="Rigioca la schedina precedente" checked={c.redColumn.usePreviousPrediction} onChange={(v) => patch('redColumn', { usePreviousPrediction: v })} />
              <Num label="Penalità" value={c.redColumn.penalty} min={-100} onChange={(v) => patch('redColumn', { penalty: v })} />
              <Check label="Riceve i bonus" checked={c.redColumn.applyBonus} onChange={(v) => patch('redColumn', { applyBonus: v })} />
              <Check label="Subisce i malus" checked={c.redColumn.applyMalus} onChange={(v) => patch('redColumn', { applyMalus: v })} />
            </fieldset>
            <fieldset className="flex flex-wrap items-end gap-4">
              <legend className="mb-2 font-semibold">Capocannoniere</legend>
              <Check label="Attivo" checked={c.topScorer.enabled} onChange={(v) => patch('topScorer', { enabled: v })} />
              <Num label="Premio totale" value={c.topScorer.totalPrize} onChange={(v) => patch('topScorer', { totalPrize: v })} />
              <label className="flex flex-col gap-1">
                <span className="font-medium">In caso di più vincitori</span>
                <Select
                  value={c.topScorer.tieRule}
                  onChange={(e) => patch('topScorer', { tieRule: e.target.value as 'anyCounts' | 'split' })}
                >
                  <option value="anyCounts">Premio pieno a ciascuno</option>
                  <option value="split">Premio diviso tra i vincitori</option>
                </Select>
              </label>
            </fieldset>
          </div>
        ) : (
          <ul className="mt-2 flex max-w-prose list-disc flex-col gap-1 pl-5">
            <li>
              Schedina non consegnata:{' '}
              {c.redColumn.usePreviousPrediction
                ? 'si rigioca la schedina precedente (stessi segni nelle stesse posizioni)'
                : 'nessun pronostico'}{' '}
              con penalità <strong>{formatPoints(c.redColumn.penalty)}</strong>
            </li>
            {c.topScorer.enabled && (
              <li>
                Capocannoniere segreto a inizio campionato: premio di{' '}
                <strong>{formatPoints(c.topScorer.totalPrize)} punti</strong>{' '}
                {c.topScorer.tieRule === 'split' ? 'diviso tra chi indovina' : 'per intero a ciascun vincitore'}
              </li>
            )}
          </ul>
        )}
      </section>

      {editing && (
        <div className="mt-10 flex gap-3">
          <Button onClick={save} loading={saving} loadingText="Salvataggio…">
            Salva nuova versione
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              setEditing(false);
              setDraft(null);
              setError(null);
              announce('Modifiche annullate.');
            }}
          >
            Annulla
          </Button>
        </div>
      )}
    </AppLayout>
  );
}

/** Input numerico compatto con label visibile. */
function Num({
  label,
  value,
  onChange,
  min = -100,
  max = 100,
  step = 0.5,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="font-medium">{label}</span>
      <input
        type="number"
        className="w-28 rounded-lg border border-ink-300 px-3 py-2 dark:border-ink-600 dark:bg-ink-900"
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </label>
  );
}

function Check({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex min-h-11 cursor-pointer items-center gap-2">
      <input
        type="checkbox"
        className="h-5 w-5 accent-panda-700"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="font-medium">{label}</span>
    </label>
  );
}
